import { useQuery, type QueryClient } from '@tanstack/react-query'
import { useMemberSession } from '@/entities/member'
import { listFileAssets, listFileEntries, listFileFolders, listFileTags, type ListFileAssetsParams } from '../api/file-api'
import type { FileAssetPage } from '../api/file-contract'

export const fileKeys = {
  all: ['files'] as const,
  list: (params: ListFileAssetsParams) => [...fileKeys.all, 'list', params] as const,
  entries: (params: ListFileAssetsParams) => [...fileKeys.all, 'entries', params] as const,
  folders: () => [...fileKeys.all, 'folders'] as const,
  tags: () => [...fileKeys.all, 'tags'] as const,
  preview: (fileId: number) => [...fileKeys.all, 'preview', fileId] as const,
}

export function useFileTags() {
  const authenticated = useMemberSession((state) => state.authenticated)
  return useQuery({ queryKey: fileKeys.tags(), queryFn: async () => (await listFileTags()).data, enabled: authenticated })
}

export function useFileAssets(page = 1, size = 50, filters: Omit<ListFileAssetsParams, 'page' | 'size'> = {}) {
  const authenticated = useMemberSession((state) => state.authenticated)
  const params = { page, size, ...filters }
  return useQuery({ queryKey: fileKeys.list(params), queryFn: async () => (await listFileAssets(params)).data, enabled: authenticated })
}

export function useFileEntries(page = 1, size = 50, filters: Omit<ListFileAssetsParams, 'page' | 'size'> = {}) {
  const authenticated = useMemberSession((state) => state.authenticated)
  const params = { page, size, ...filters }
  return useQuery({ queryKey: fileKeys.entries(params), queryFn: async () => (await listFileEntries(params)).data, enabled: authenticated })
}

export function useFileFolderTree() {
  const authenticated = useMemberSession((state) => state.authenticated)
  return useQuery({ queryKey: fileKeys.folders(), queryFn: async () => (await listFileFolders()).data, enabled: authenticated })
}

export function updateFileFavoriteCache(queryClient: QueryClient, fileId: number, favorite: boolean) {
  queryClient.getQueriesData<FileAssetPage>({ queryKey: fileKeys.all }).forEach(([queryKey, page]) => {
    if (queryKey[1] !== 'list' || !page) return
    const params = queryKey[2] as ListFileAssetsParams | undefined
    const containsFile = page.items.some((item) => item.fileId === fileId)
    if (!containsFile) return
    const excludedByFavoriteFilter = params?.favorite !== undefined && params.favorite !== favorite
    queryClient.setQueryData<FileAssetPage>(queryKey, {
      ...page,
      items: excludedByFavoriteFilter
        ? page.items.filter((item) => item.fileId !== fileId)
        : page.items.map((item) => item.fileId === fileId ? { ...item, favorite } : item),
      total: excludedByFavoriteFilter ? Math.max(0, page.total - 1) : page.total,
    })
  })
}
